import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import { useAuth } from '../../lib/auth'
import { useGatedAction } from '../../lib/useSubscription'
import {
  startFakeoutEndless, serveFakeoutEndless, pickFakeoutEndless,
  fakeoutEndlessResults,
  type FakeoutEndlessServe, type FakeoutEndlessPick, type FakeoutEndlessResults,
} from '../../lib/api/wordEndless'
import {
  hostWordTvRoom, joinWordTvRoom, fetchWordTvRoom, startWordTvRoom,
  leaveWordTvRoom, nextWordRound, skipWordSeat, type WordTvSeat,
} from '../../lib/api/wordTv'
import { EndlessSetup, PassDevice, EndlessOutro, RoomLobby, RoundScoreboard, type SetupMode } from './EndlessSetup'
import { HowToOverlay } from './HowToOverlay'

const TITLE = 'FAKE OUT'
const ACCENT = '#6B8A34'
const BORDER = '#A2C66D'
const GLASS = 'btn-glass-fakeout'

const HOW_TO = [
  'Six words a round. Each shows four definitions, and one of them is real.',
  'A right answer is worth 100 points, and every second you take costs 5 of them. A right answer is never worth less than 20.',
  'Spot all six for a 200 point bonus.',
  'Play solo, pass the phone, or open a private room: everyone answers the same six words and the highest score wins.',
  'In a room, up to 16 people play in parallel on their own phones. Only the host needs a subscription, so guests play free.',
  'You never see the same word twice, in this mode or the daily.',
]

type Phase = 'setup' | 'lobby' | 'pass' | 'playing' | 'reveal' | 'results' | 'room_done'

interface Props {
  onBack: () => void
  onPlayWeekly: () => void
  onOpenPaywall: () => void
  /** Set when arriving with a room code (QR scan, push, Jump back in) —
   *  auto-claims (or re-claims) a seat on mount. */
  resumeCode?: string
  /**
   * Fired when the auto-join is ATTEMPTED, not when it succeeds. A code
   * that outlives a failed attempt makes every later visit to this game
   * jump straight back into a room instead of showing the chooser.
   */
  onResumeConsumed?: () => void
}

/**
 * FAKE OUT, endless. Same hidden per-word clock and scoring as the
 * daily, but dealt from the pool and replayable. Pass-and-play answers
 * the SAME six words in turn on one phone; a private room deals the
 * same six words to every seat and everyone plays in parallel on their
 * own phone — nothing but finish-state and scores shows until the
 * round is over.
 */
export function FakeOutEndlessScreen({
  onBack, onPlayWeekly, onOpenPaywall, resumeCode, onResumeConsumed,
}: Props) {
  const t = useT()
  const { profile } = useAuth()
  const gate = useGatedAction(onOpenPaywall)
  const [phase, setPhase] = useState<Phase>('setup')
  const [session, setSession] = useState<string | null>(null)
  const [players, setPlayers] = useState(1)
  const [names, setNames] = useState<string[]>([])
  const [player, setPlayer] = useState(0)
  const [position, setPosition] = useState(1)
  const [served, setServed] = useState<FakeoutEndlessServe | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [last, setLast] = useState<FakeoutEndlessPick | null>(null)
  const [score, setScore] = useState(0)
  const [results, setResults] = useState<FakeoutEndlessResults | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showHowTo, setShowHowTo] = useState(false)
  const [setupMode, setSetupMode] = useState<SetupMode>('choose')
  // Room mode. null = solo / pass-and-play.
  const [roomCode, setRoomCode] = useState<string | null>(null)
  const [isHost, setIsHost] = useState(false)
  const [roomSeats, setRoomSeats] = useState<WordTvSeat[]>([])
  const [roomStatus, setRoomStatus] = useState<'lobby' | 'playing' | 'finished' | 'abandoned'>('lobby')
  const [roomRounds, setRoomRounds] = useState(1)
  const [roomRound, setRoomRound] = useState(1)
  const [leaveArmed, setLeaveArmed] = useState(false)

  async function begin(seats: string[]) {
    if (busy) return
    const n = Math.max(1, seats.length)
    setBusy(true); setError(null)
    try {
      const id = await startFakeoutEndless(n)
      setSession(id); setPlayers(n); setNames(seats)
      setPlayer(0); setPosition(1); setScore(0)
      setPhase(seats.length > 1 ? 'pass' : 'playing')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** The name typed to join a party, if there is one. */
  function typedJoinName(): string {
    try { return localStorage.getItem('jdg_player_name')?.trim() ?? '' } catch { return '' }
  }

  /**
   * Sitting down in someone else's room uses the name typed for THAT
   * join, falling back to the account name. Party names are per-room by
   * design — they never touch the profile. Empty is meaningful: the
   * server keeps whatever the seat already had.
   */
  function joinSeatName(): string {
    return typedJoinName() || profile?.display_name?.trim() || ''
  }

  /** Hosting is always you, so seat 0 takes the account name. */
  function hostSeatName(): string {
    return profile?.display_name?.trim() || typedJoinName() || ''
  }

  /**
   * Open a private room: create the session on our own account (that is
   * where the All-Access check lives), then mint a code around it.
   * Guests pay nothing — one subscriber opens the table.
   */
  async function hostRoom(seatCount: number, rounds: number) {
    if (busy) return
    setBusy(true); setError(null)
    try {
      const id = await startFakeoutEndless(seatCount)
      const code = await hostWordTvRoom('fake_out', id, hostSeatName(), rounds)
      const room = await fetchWordTvRoom(code)
      setSession(id); setPlayers(seatCount); setPlayer(0)
      setNames((room?.seats ?? []).map(s => s.name))
      setRoomCode(code); setIsHost(true)
      setRoomSeats(room?.seats ?? []); setRoomStatus(room?.status ?? 'lobby')
      setRoomRounds(room?.rounds ?? rounds); setRoomRound(room?.current_round ?? 1)
      setPosition(1); setScore(0)
      setPhase('lobby')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** Claim (or re-claim) a seat in a room and play it on this phone. */
  async function joinRoom(code: string) {
    if (busy) return
    setBusy(true); setError(null)
    try {
      const j = await joinWordTvRoom(code, joinSeatName())
      // Read the room back rather than guessing: the seat COUNT decides
      // the header, and the seat NAMES are what the TV is showing, so
      // the phone has to agree with the big screen.
      const room = await fetchWordTvRoom(j.code)
      const seats = room?.seats ?? []
      const status = (room?.status ?? 'lobby') as 'lobby' | 'playing' | 'finished' | 'abandoned'
      setSession(j.session); setPlayer(j.player)
      setPlayers(seats.length || 1)
      setNames(seats.map(s => s.name))
      setRoomCode(j.code)
      setIsHost(typeof room?.host_seat === 'number' ? room.host_seat === j.player : false)
      setRoomSeats(seats); setRoomStatus(status)
      setRoomRounds(room?.rounds ?? 1); setRoomRound(room?.current_round ?? 1)
      setPosition(1); setScore(0)
      if (status === 'playing') await enterRoomPlay(j.session, j.player)
      else setPhase('lobby')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /**
   * Enter (or re-enter) room play: fast-forward past positions this
   * seat already answered, so a resume lands on the next unplayed word
   * instead of re-dealing word one.
   */
  async function enterRoomPlay(sess: string, seat: number) {
    // The playing effect serves off the session STATE — keep it in sync
    // when a round flip hands us a fresh session.
    setSession(sess)
    setResults(null)
    let pos = 1
    let total = 0
    for (; pos <= 6; pos++) {
      const s = await serveFakeoutEndless(sess, seat, pos)
      if (!s.answered) break
      total += s.pts ?? 0
    }
    setScore(total)
    if (pos > 6) { setPhase('room_done'); return }
    setPosition(pos)
    setPhase('playing')
  }

  /** Host only: flips the room to playing (fires the push) and deals in. */
  async function startRoom() {
    if (!roomCode || busy) return
    setBusy(true); setError(null)
    try {
      await startWordTvRoom(roomCode)
      setRoomStatus('playing')
      setPhase('playing')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /**
   * Leave the room (host leaving abandons it) and reset to the menu.
   * Host closes with a two-tap arm instead of window.confirm — embedded
   * webviews swallow confirm() and the button reads as dead.
   */
  async function leaveRoomToSetup() {
    if (!roomCode) return
    if (isHost && !leaveArmed) {
      setLeaveArmed(true)
      setTimeout(() => setLeaveArmed(false), 3500)
      return
    }
    try { await leaveWordTvRoom(roomCode) } catch { /* best effort */ }
    playAgain()
  }

  /** Host only: deal the next round once the table is done. */
  async function startNextRound() {
    if (!roomCode || busy) return
    setBusy(true); setError(null)
    try {
      const r = await nextWordRound(roomCode)
      setRoomRound(r.current_round)
      await enterRoomPlay(r.session, player)
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** Host only: skip/unskip a straggler so the table can move on. */
  async function toggleSkip(seat: WordTvSeat) {
    if (!roomCode || !isHost) return
    try {
      await skipWordSeat(roomCode, seat.player, !seat.skipped)
      setRoomSeats(prev => prev.map(x =>
        x.player === seat.player ? { ...x, skipped: !seat.skipped } : x))
    } catch (e) { setError(readError(e)) }
  }

  // Arrived with a code (QR scan, push tap, Jump back in): claim the
  // seat straight away so the guest lands in the game rather than on a
  // setup screen they did not ask for. The ref guards StrictMode's
  // double-effect from burning two seats.
  const autoJoined = useRef(false)
  useEffect(() => {
    if (!resumeCode || autoJoined.current) return
    autoJoined.current = true
    onResumeConsumed?.()
    void joinRoom(resumeCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCode])

  // Room poll while waiting (lobby) or watching others finish
  // (room_done). Realtime is useless here — the endless tables have RLS
  // with zero policies, so a subscription would deliver silence.
  useEffect(() => {
    if (!roomCode || (phase !== 'lobby' && phase !== 'room_done')) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const tick = async () => {
      try {
        const r = await fetchWordTvRoom(roomCode)
        if (cancelled) return
        if (r) {
          setRoomSeats(r.seats)
          setRoomStatus(r.status)
          setRoomRounds(r.rounds ?? 1)
          setRoomRound(r.current_round ?? 1)
          setNames(r.seats.map(s => s.name))
          setPlayers(r.seats.length || 1)
          // Hand-off support: the server may promote another seat to
          // host when the original host leaves. Trust the payload over
          // local state so the promoted phone grows the host controls.
          if (typeof r.host_seat === 'number') setIsHost(r.host_seat === player)
          if (r.status === 'abandoned') {
            setError(t('The host closed this room.'))
            playAgain()
            return
          }
          if (phase === 'lobby' && r.status === 'playing' && !isHost && session) {
            await enterRoomPlay(r.session ?? session, player)
            return
          }
          // The host advanced while we sat on the scoreboard: follow
          // the table into the fresh round.
          if (phase === 'room_done' && r.status === 'playing' && r.session && session
              && r.session !== session) {
            await enterRoomPlay(r.session, player)
            return
          }
        }
      } catch { /* transient; the next tick retries */ }
      if (!cancelled) timer = setTimeout(tick, 2500)
    }
    void tick()
    return () => { cancelled = true; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, phase, isHost, session, player])

  // No auto-close, and no host close button. Owner rule (2026-08-15): a
  // host leaves a room, they do not shut it down on everyone else. A room
  // ends when its last seat leaves (abandon_humanless_rooms), when a
  // lobby goes an hour without starting, or never. word_endless_finish is
  // a server-side no-op for the same reason.


  // The words only reveal once the whole table is done (or the host
  // closed the room) — mid-round, scores are all anyone sees.
  const roomActive = roomSeats.filter(s => s.joined && !s.skipped)
  const roomRoundOver = roomStatus === 'finished'
    || (roomActive.length > 0 && roomActive.every(s => s.done >= 6))
  useEffect(() => {
    if (phase !== 'room_done' || !session || !roomRoundOver || results) return
    let cancelled = false
    fakeoutEndlessResults(session)
      .then(r => { if (!cancelled) setResults(r) })
      .catch(() => { /* standings still show; reveal retries next poll */ })
    return () => { cancelled = true }
  }, [phase, session, roomRoundOver, results])







  /** Seat label: the typed name in pass-and-play, "You" when solo. */
  function seatName(i: number): string {
    return names[i]?.trim() || (players > 1 ? t('Player {n}', { n: i + 1 }) : t('You'))
  }

  /**
   * The round-by-round breakdown, and the actual payoff of the game:
   * the real definition, who spotted it, and exactly which fake each
   * person fell for. It used to print only the real definition, which
   * read as an answer key and told you nothing about how you or anyone
   * else did.
   *
   * `picks` is optional because an older server omits it; in that case
   * this degrades to the previous behaviour rather than rendering empty.
   */
  function RoundBreakdown({ words }: { words: FakeoutEndlessResults['words'] }) {
    return (
      <div className="space-y-2.5 mt-4">
        {words.map((w, wi) => {
          const picks = w.picks ?? []
          const got = picks.filter(p => p.correct)
          const missed = picks.filter(p => !p.correct)
          return (
            <div key={`${w.word}-${wi}`} className="card border border-brand-border p-4">
              <p className="font-display font-bold uppercase tracking-wide text-brand-text">
                {w.word}
              </p>

              <div className="mt-2 rounded-xl border-2 px-3 py-2.5"
                   style={{ borderColor: '#2E7D32', backgroundColor: '#2E7D3214' }}>
                <p className="text-sm text-brand-text">{w.real_def}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest mt-1.5"
                   style={{ color: '#2E7D32' }}>
                  {got.length
                    ? `${t('The real one')} · ${got.map(p => seatName(p.player)).join(', ')}`
                    : `${t('The real one')} · ${t('nobody got it')}`}
                </p>
              </div>

              {missed.map(p => (
                <div key={p.player}
                     className="flex items-start gap-2 mt-1.5 rounded-xl bg-brand-elevated px-3 py-2">
                  <span className="text-xs font-semibold text-brand-muted shrink-0 max-w-[35%] truncate">
                    {seatName(p.player)}
                  </span>
                  <span className="flex-1 text-xs text-brand-muted">
                    {p.pick ?? t('no answer')}
                  </span>
                </div>
              ))}
            </div>
          )
        })}
      </div>
    )
  }

  // Serve the current word whenever we enter play for a new position.
  useEffect(() => {
    if (phase !== 'playing' || !session) return
    let cancelled = false
    setServed(null); setSelected(null)
    serveFakeoutEndless(session, player, position)
      .then(s => { if (!cancelled) setServed(s) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [phase, session, player, position])

  async function lockIn() {
    if (!session || !selected || busy) return
    setBusy(true)
    try {
      const r = await pickFakeoutEndless(session, player, position, selected)
      setLast(r); setScore(r.score); setPhase('reveal')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  async function next() {
    if (!session) return
    setLast(null)
    if (position < 6) { setPosition(position + 1); setPhase('playing'); return }
    // This seat is done. In a room everyone else is playing in parallel
    // on their own phone — go watch the scoreboard instead of passing.
    if (roomCode) { setPhase('room_done'); return }
    if (player + 1 < players) {
      setPlayer(player + 1); setPosition(1); setScore(0); setPhase('pass')
      return
    }
    setBusy(true)
    try {
      setResults(await fakeoutEndlessResults(session))
      setPhase('results')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  function playAgain() {
    setSession(null); setResults(null); setLast(null); setServed(null)
    setPlayer(0); setPosition(1); setScore(0)
    setRoomCode(null); setIsHost(false); setRoomSeats([]); setRoomStatus('lobby')
    setRoomRounds(1); setRoomRound(1); setLeaveArmed(false)
    setSetupMode('choose')
    setPhase('setup')
  }

  return (
    <div className="screen">
      <BackgroundTitle text={TITLE} color="#8FAF52" />
      <PageHeader
        title={TITLE}
        subtitle={players > 1 && phase !== 'setup' && phase !== 'results'
          ? t('{name} · {n} of {total}', { name: seatName(player), n: player + 1, total: players })
          : t('One of these definitions is telling the truth')}
        onBack={phase === 'setup' && setupMode !== 'choose'
          ? () => setSetupMode('choose')
          : onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-8 relative z-10 glass-panes">
        <button
          type="button"
          onClick={() => setShowHowTo(true)}
          className="w-full mb-3 py-2.5 rounded-full border-2 border-brand-border bg-white
                     text-xs font-semibold uppercase tracking-widest text-brand-muted
                     active:scale-[0.98] transition-transform"
        >
          {t('How to play')}
        </button>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}


        {/* The host reads this out; a guest sees which room they are in.
            Kept above the play area so it survives every phase. */}
        {roomCode && phase !== 'setup' && phase !== 'lobby' && (
          <div className="card flex items-center justify-between gap-3 mb-3 rounded-2xl border-2 px-4 py-2.5"
               style={{ borderColor: BORDER }}>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
              {roomRounds > 1 ? t('Round {n} of {total}', { n: roomRound, total: roomRounds }) : t('Room')}
            </span>
            <span className="font-display font-black text-xl tracking-[0.2em]"
                  style={{ color: ACCENT }}>
              {roomCode}
            </span>
          </div>
        )}

        {phase === 'setup' && (
          <EndlessSetup
            accent={ACCENT} border={BORDER} glassClass={GLASS}
            mode={setupMode} onModeChange={setSetupMode}
            gameImage="/games/fake-out.webp" busy={busy}
            onStart={seats => gate(() => begin(seats))}
            onHostRoom={(n, r) => gate(() => hostRoom(n, r))}
            onJoinRoom={joinRoom}
            onPlayWeekly={onPlayWeekly}
          />
        )}

        {phase === 'lobby' && roomCode && (
          <RoomLobby
            code={roomCode} game="fake_out" accent={ACCENT} border={BORDER}
            glassClass={GLASS} isHost={isHost} seats={roomSeats} busy={busy}
            leaveArmed={leaveArmed}
            onStart={() => void startRoom()}
            onLeave={() => void leaveRoomToSetup()}
          />
        )}

        {phase === 'room_done' && (
          <>
            <div className="card p-4 mt-1 rounded-2xl text-center border-2 bg-game-fakeout-light"
                 style={{ borderColor: BORDER }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {!roomRoundOver ? t('Waiting for everyone to finish…')
                  : roomRound < roomRounds ? t('Round {n} of {total}', { n: roomRound, total: roomRounds })
                  : t('Final standings')}
              </p>
              <div className="mt-2 space-y-1.5">
                {[...roomSeats].filter(s => s.joined)
                  .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
                  .map((s, i) => (
                    <div key={s.player}
                         className="card flex items-center gap-3 border border-brand-border px-3 py-2">
                      <span className="w-7 text-center font-display font-bold"
                            style={{ color: i === 0 ? ACCENT : '#6B7280' }}>
                        {i + 1}
                      </span>
                      <span className={`flex-1 text-left text-sm font-semibold truncate
                                        ${s.skipped ? 'text-brand-muted line-through' : 'text-brand-text'}`}>
                        {s.name}{s.player === player ? ` · ${t('You')}` : ''}
                      </span>
                      {isHost && !roomRoundOver && s.player !== player && (
                        <button
                          type="button"
                          onClick={() => void toggleSkip(s)}
                          className="text-[10px] font-semibold uppercase tracking-widest
                                     text-brand-muted underline shrink-0"
                        >
                          {s.skipped ? t('Unskip') : t('Skip')}
                        </button>
                      )}
                      <span className="text-xs text-brand-muted">{s.done}/6</span>
                      <span className="font-display font-bold text-brand-text">{s.score ?? 0}</span>
                    </div>
                  ))}
              </div>
            </div>

            {results && roomRoundOver && (
              <RoundBreakdown words={results.words} />
            )}

            {/* The whole game's arithmetic, once the last round is done:
                every round's points per player, ranked. Between rounds
                the seat list above is the running total, which is what
                you want mid-game. */}
            {roomRoundOver && roomRound >= roomRounds && (
              <RoundScoreboard seats={roomSeats} accent={ACCENT} border={BORDER}
                               youPlayer={player} />
            )}

            {/* Leave is ALWAYS available and is the only way out. A host
                cannot close a room other people are sitting in (owner
                rule): the room ends when everyone leaves.

                Next round is shown to the host whenever rounds remain,
                DISABLED until the table is done rather than hidden. It
                used to be hidden, which left a waiting host looking at a
                single "Finish room" button and no way forward — the Skip
                links that unblock it are up in the seat list and easy to
                miss, so the hint names them. */}
            {roomStatus === 'finished' ? (
              <EndlessOutro glassClass={GLASS} accent={ACCENT}
                            onPlayAgain={playAgain} onBackToGames={onBack} />
            ) : (
              <>
                {isHost && roomRound < roomRounds && (
                  <button
                    type="button"
                    disabled={busy || !roomRoundOver}
                    onClick={() => void startNextRound()}
                    className={`${GLASS} w-full mt-5 py-4 text-center font-display font-bold text-xl
                                active:scale-[0.98] transition-transform disabled:opacity-40`}
                  >
                    {busy ? t('Starting…') : t('Next round')}
                  </button>
                )}
                {isHost && roomRound < roomRounds && !roomRoundOver && (
                  <p className="text-center text-brand-muted text-xs mt-2">
                    {t('Everyone has to finish first. Skip anyone who is stuck.')}
                  </p>
                )}
                {!isHost && roomRoundOver && roomRound < roomRounds && (
                  <p className="text-center text-brand-muted text-sm animate-pulse mt-5">
                    {t('Waiting for the host to start the next round…')}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void leaveRoomToSetup()}
                  className={`${GLASS} w-full mt-3 py-3.5 text-center font-display font-bold text-lg
                              active:scale-[0.98] transition-transform`}
                  style={{ color: ACCENT }}
                >
                  {t('Leave room')}
                </button>
              </>
            )}
          </>
        )}

        {phase === 'pass' && (
          <PassDevice name={seatName(player)} accent={ACCENT} border={BORDER}
                      onReady={() => setPhase('playing')} />
        )}

        {phase === 'playing' && !served && (
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
        )}

        {phase === 'playing' && served && !served.answered && (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mt-1">
              {t('Word {a} of {b}', { a: position, b: 6 })}
            </p>
            <h2 className="font-display font-bold text-4xl text-brand-text uppercase tracking-wide mt-1 mb-4">
              {served.word}
            </h2>
            <div className="space-y-2.5">
              {served.defs?.map(def => (
                <button
                  key={def}
                  onClick={() => setSelected(def)}
                  className={`w-full rounded-2xl p-4 text-left text-sm leading-relaxed
                              border-2 transition-colors
                              ${selected === def
                                ? 'border-brand-text bg-brand-elevated font-semibold'
                                : 'border-brand-border bg-white'}`}
                >
                  {def}
                </button>
              ))}
            </div>
            <button
              onClick={() => void lockIn()}
              disabled={!selected || busy}
              className="w-full mt-5 py-3.5 rounded-2xl font-display font-bold text-lg
                         bg-brand-text text-white active:scale-[0.98] transition-transform
                         disabled:opacity-40"
            >
              {busy ? t('Checking…') : t('Lock it in')}
            </button>
          </>
        )}

        {phase === 'reveal' && last && (
          <>
            <div className={`card p-4 mt-1 rounded-2xl border-2 ${last.correct ? 'border-brand-success' : 'border-brand-danger'}`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-bold uppercase tracking-wide text-brand-text">
                  {last.word}
                </p>
                <span className={`font-display font-bold ${last.correct ? 'text-brand-success' : 'text-brand-danger'}`}>
                  {last.correct ? `+${last.pts}` : t('FOOLED')}
                </span>
              </div>
              <p className="text-sm text-brand-text mt-1.5">{last.real_def}</p>
            </div>
            <button
              onClick={() => void next()}
              disabled={busy}
              className="w-full mt-4 py-3.5 rounded-2xl font-display font-bold text-lg
                         bg-brand-text text-white active:scale-[0.98] transition-transform
                         disabled:opacity-40"
            >
              {position < 6
                ? t('Next word')
                : roomCode ? t('See standings')
                : player + 1 < players ? t('Pass the phone') : t('See results')}
            </button>
            <p className="text-center text-xs text-brand-muted mt-3">
              {t('{n} pts so far', { n: score })}
            </p>
          </>
        )}

        {phase === 'results' && results && (
          <>
            <div className="card p-4 mt-1 rounded-2xl text-center border-2 bg-game-fakeout-light"
                 style={{ borderColor: BORDER }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {results.players > 1 ? t('Final standings') : t('Final results')}
              </p>
              <div className="mt-2 space-y-1.5">
                {results.scores.map((s, i) => (
                  <div key={s.player}
                       className="card flex items-center gap-3 border border-brand-border px-3 py-2">
                    <span className="w-7 text-center font-display font-bold"
                          style={{ color: i === 0 ? ACCENT : '#6B7280' }}>
                      {i + 1}
                    </span>
                    <span className="flex-1 text-left text-sm font-semibold text-brand-text">
                      {seatName(s.player)}
                    </span>
                    <span className="text-xs text-brand-muted">
                      {t('{a} of {b} spotted', { a: s.correct, b: 6 })}
                    </span>
                    <span className="font-display font-bold text-brand-text">{s.score}</span>
                  </div>
                ))}
              </div>
            </div>

            <RoundBreakdown words={results.words} />

            <EndlessOutro glassClass={GLASS} accent={ACCENT}
                          onPlayAgain={playAgain} onBackToGames={onBack} />
          </>
        )}
      </div>

      {showHowTo && (
        <HowToOverlay title={TITLE} lines={HOW_TO.map(l => t(l))} accent={ACCENT}
                      image="/games/fake-out.webp" onClose={() => setShowHowTo(false)} />
      )}
    </div>
  )
}
